import type { PositionRowId } from '../../../types';
import { formatGermanNumber } from '../../../utils/parseGermanNumber';
import { formatEuro } from './offerDocumentUtils';
import { contributesToOfferNetTotal, isOfferMetaRowKind } from './offerEventualPosition';
import { normalizeOfferTargetMarginPercent } from './offerTargetMargin';
import {
  computeOfferCalculationRowFigures,
  computeOfferCalculationSummary,
  type OfferCalculationSummaryRow,
} from './offerCalculationSummary';
import {
  effectiveLaborMinutes,
  isHourLaborPricedRow,
  isVirtualTitleLaborRow,
  MINUTES_PER_LABOR_HOUR,
  normalizeHourlyRate,
  parseHourlyRateOverride,
} from './offerLaborPricing';

/** Höchstzahl gespeicherter ausgeblendeter Hinweise je Entwurf. */
export const CALCULATION_DISMISSED_HINTS_MAX = 500;

/** Beträge unter einem halben Cent zählen nicht als Abweichung. */
const EPS = 0.005;

export type OfferMarginHintKind =
  | 'below_target'
  | 'negative_profit'
  | 'hourly_rate_below_cost'
  | 'labor_cost_missing'
  | 'row_loss'
  | 'row_no_surcharge'
  | 'row_vk_without_ek'
  | 'row_hourly_rate_below_cost'
  | 'cheaper_quote';

export type OfferMarginHintSeverity = 'info' | 'warn';

export type OfferMarginHint = {
  /** Stabil je Art und Position, damit „Ausblenden“ einen Reload übersteht. */
  id: string;
  kind: OfferMarginHintKind;
  severity: OfferMarginHintSeverity;
  title: string;
  detail: string;
  /** Index in den übergebenen Zeilen (entspricht `data-offer-row-index` der Kalkulationsliste). */
  rowIndex?: number;
  rowId?: string;
  /** Backend-Position der Zeile, falls vorhanden. */
  positionId?: string;
  numberLabel?: string;
  /**
   * Euro-Betrag des Hinweises: fehlender Betrag bis zur Ziel-Marge bzw. Kostendeckung,
   * Verlust, Material-VK ohne EK oder mögliche Ersparnis.
   */
  amount?: number;
};

/** Zeile für die Hinweise: Kalkulationsfelder plus Bezeichnung und Positionsbezug. */
export type OfferMarginHintRow = OfferCalculationSummaryRow & {
  item_name?: string | null;
  numberLabel?: string | null;
  leadPositionId?: PositionRowId | null;
  isApi?: boolean;
};

/** Kompakte Sicht auf Lieferantenangebote je Position (siehe Kalkulationsliste). */
export type OfferMarginQuoteSummary = {
  cheapestEk: number | null;
  cheapestSupplier: string | null;
};

export type OfferMarginHintsInput = {
  rows: readonly OfferMarginHintRow[];
  hourlyRate: number;
  realLaborCostRate: number;
  /** Ziel-Marge aus den Firmeneinstellungen; `null` = keine Ziel-Hinweise. */
  targetMarginPercent?: number | null;
  /** Nur wenn die Angebote schon geladen sind — die Hinweise laden nichts nach. */
  quoteOffersByPositionId?: Record<string, OfferMarginQuoteSummary> | null;
};

/** Ausgeblendete Hinweise aus dem Entwurf: eindeutige, nicht leere IDs, begrenzt. */
export function normalizeCalculationDismissedHints(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of raw) {
    if (typeof value !== 'string') continue;
    const id = value.trim().slice(0, 200);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out.slice(-CALCULATION_DISMISSED_HINTS_MAX);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatPercent(n: number, fractionDigits = 2): string {
  return `${formatGermanNumber(n, { minimumFractionDigits: 0, maximumFractionDigits: fractionDigits })} %`;
}

function formatRate(n: number): string {
  return `${formatGermanNumber(n)} €/h`;
}

function formatHours(n: number): string {
  return `${formatGermanNumber(n, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} Std`;
}

/** Positions-Schlüssel für die Hinweis-ID: Backend-Position vor Zeilen-ID (Draft-Overlay ändert die Zeilen-ID). */
function rowKey(row: OfferMarginHintRow): string {
  if (row.leadPositionId != null && String(row.leadPositionId).trim()) return `p${row.leadPositionId}`;
  return row.id;
}

function rowLabel(row: OfferMarginHintRow): string {
  const label = String(row.numberLabel ?? '').trim();
  if (label) return `Pos. ${label}`;
  const name = String(row.item_name ?? '').trim();
  if (name) return name.length > 40 ? `${name.slice(0, 39)}…` : name;
  return 'Position';
}

function rowName(row: OfferMarginHintRow): string {
  const label = String(row.numberLabel ?? '').trim();
  const name = String(row.item_name ?? '').trim();
  if (!label || !name) return '';
  return name.length > 60 ? `${name.slice(0, 59)}…` : name;
}

/** Positionszeilen mit eigenen Preisen: keine Titel/Texte/Bilder, Zuschlagszeilen oder virtuelle Lohnzeilen. */
function isPricedPositionRow(row: OfferMarginHintRow): boolean {
  if (isOfferMetaRowKind(row.kind)) return false;
  if (row.kind === 'positionSurcharge') return false;
  return !isVirtualTitleLaborRow(row);
}

/**
 * Nicht blockierende Hinweise zur Kalkulation (Marge unter Ziel, Verlustpositionen, fehlende
 * Kosten, …). Reine Funktion ohne API-Aufrufe; dieselbe Logik nutzen Editor und KI.
 */
export function computeOfferMarginHints(input: OfferMarginHintsInput): OfferMarginHint[] {
  const rows = input.rows;
  const hourlyRate = normalizeHourlyRate(input.hourlyRate);
  const realLaborCostRate = Math.max(0, Number(input.realLaborCostRate) || 0);
  const target = normalizeOfferTargetMarginPercent(input.targetMarginPercent);
  const rates = { hourlyRate, realLaborCostRate };
  const summary = computeOfferCalculationSummary(rows, rates);
  const hints: OfferMarginHint[] = [];

  // 1) Gesamtmarge unter Ziel: Ziel-VK = Kosten ÷ (1 − Ziel). Verglichen wird die angezeigte
  // (auf 2 Stellen gerundete) Marge, damit Hinweis und Kalkulationsübersicht übereinstimmen.
  if (target != null && summary.margin != null && summary.margin < target && summary.totalCosts > EPS) {
    const targetVk = summary.totalCosts / (1 - target / 100);
    const missing = targetVk - summary.vk;
    if (missing > EPS) {
      const margin = summary.margin;
      const options: string[] = [];
      if (summary.materialEk > EPS) {
        const points = (missing / summary.materialEk) * 100;
        options.push(`rund ${formatGermanNumber(points, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} Prozentpunkte mehr Aufschlag auf das Material`);
      }
      if (summary.laborHours > 0) {
        options.push(`rund ${formatRate(missing / summary.laborHours)} mehr Stundensatz`);
      }
      hints.push({
        id: 'below_target',
        kind: 'below_target',
        severity: 'warn',
        title: `Marge ${formatPercent(margin)} unter Ziel ${formatPercent(target)}`,
        detail:
          `Für ${formatPercent(target)} fehlen ${formatEuro(missing)} netto (Ziel-Verkauf ${formatEuro(targetVk)}).` +
          (options.length > 0 ? ` Das entspricht ${options.join(' oder ')}.` : ''),
        amount: round2(missing),
      });
    }
  }

  // 2) Negativer Rohertrag.
  if (summary.profit < -EPS) {
    hints.push({
      id: 'negative_profit',
      kind: 'negative_profit',
      severity: 'warn',
      title: `Rohertrag negativ: ${formatEuro(summary.profit)}`,
      detail: `Die Kosten (${formatEuro(summary.totalCosts)}) liegen über dem Verkauf netto (${formatEuro(summary.vk)}). Bis zur Kostendeckung fehlen ${formatEuro(-summary.profit)}.`,
      amount: round2(-summary.profit),
    });
  }

  // 3) Standard-Stundensatz unter den Reallohnkosten (nur Lohnzeit ohne eigenen Satz).
  let defaultRateHours = 0;
  for (const row of rows) {
    if (!isPricedPositionRow(row) || !contributesToOfferNetTotal(row)) continue;
    if (parseHourlyRateOverride(row.hourly_rate) != null) continue;
    defaultRateHours += (row.quantity * effectiveLaborMinutes(row)) / MINUTES_PER_LABOR_HOUR;
  }
  if (realLaborCostRate > 0 && defaultRateHours > 0 && hourlyRate < realLaborCostRate - EPS) {
    const gap = realLaborCostRate - hourlyRate;
    const loss = defaultRateHours * gap;
    hints.push(
      hourlyRate > 0
        ? {
            id: 'hourly_rate_below_cost',
            kind: 'hourly_rate_below_cost',
            severity: 'warn',
            title: 'Stundensatz unter Reallohnkosten',
            detail: `${formatRate(hourlyRate)} Verkauf bei ${formatRate(realLaborCostRate)} Reallohnkosten: jede Lohnstunde bringt ${formatEuro(gap)} weniger ein, als sie kostet (bei ${formatHours(defaultRateHours)} zusammen ${formatEuro(loss)}).`,
            amount: round2(loss),
          }
        : {
            id: 'hourly_rate_below_cost',
            kind: 'hourly_rate_below_cost',
            severity: 'info',
            title: 'Lohnzeit ohne Stundensatz',
            detail: `${formatHours(defaultRateHours)} Lohnzeit erscheinen nicht im Verkaufspreis, kosten aber ${formatEuro(loss)} Reallohn. Stundensatz setzen oder den Lohn im Material-VK einpreisen.`,
            amount: round2(loss),
          },
    );
  }

  // 4) Lohnzeit ohne Reallohnkosten: der Lohnanteil zählt voll als Rohertrag.
  if (summary.laborMinutes > 0 && realLaborCostRate <= 0) {
    hints.push({
      id: 'labor_cost_missing',
      kind: 'labor_cost_missing',
      severity: 'info',
      title: 'Reallohnkosten fehlen',
      detail: `${formatHours(summary.laborHours)} Lohnzeit sind kalkuliert, ohne Reallohnkosten zählt der Lohnanteil (${formatEuro(summary.laborShare)}) voll als Rohertrag. Reallohnkosten unter „Kalkulationsparameter“ eintragen.`,
    });
  }

  // 5) Positionen.
  rows.forEach((row, rowIndex) => {
    if (!isPricedPositionRow(row)) return;
    const key = rowKey(row);
    const label = rowLabel(row);
    const name = rowName(row);
    const prefix = name ? `${name}: ` : '';
    const base = {
      rowIndex,
      rowId: row.id,
      ...(row.leadPositionId != null ? { positionId: String(row.leadPositionId) } : {}),
      ...(row.numberLabel ? { numberLabel: String(row.numberLabel) } : {}),
    };
    const hourLabor = isHourLaborPricedRow(row);
    // Wirtschaftliche Sicht unabhängig von der Darstellung: Material-VK + Lohnanteil.
    const figures = computeOfferCalculationRowFigures(row, { ...rates, listLaborSeparately: false });
    const revenue = figures.lineTotal;
    const costs = figures.rowCosts;
    const materialRevenue = row.quantity * figures.materialVk;
    const materialCosts = row.quantity * figures.materialEk;
    const override = parseHourlyRateOverride(row.hourly_rate);
    const laborUnderCost =
      figures.laborHours > 0 && realLaborCostRate > 0 && figures.hourlyRate < realLaborCostRate - EPS;
    const materialUnderCost = materialRevenue < materialCosts - EPS;

    // Verlust: nur wenn nicht allein der Stundensatz schuld ist (dafür gibt es eigene Hinweise).
    // Zeilen ohne Kosten (z. B. Nachlass mit negativem Preis) sind Abzüge, keine Verluste.
    const loss = costs - revenue;
    const isLoss = loss > EPS && costs > EPS && (materialUnderCost || !laborUnderCost);
    if (isLoss) {
      hints.push({
        ...base,
        id: `row_loss:${key}`,
        kind: 'row_loss',
        severity: 'warn',
        title: `${label}: Verkauf unter Kosten`,
        detail: `${prefix}${formatEuro(revenue)} Verkauf bei ${formatEuro(costs)} Kosten (${formatEuro(-loss)}).`,
        amount: round2(loss),
      });
    }

    if (!hourLabor && row.unit_cost_ek > EPS && row.unit_price_vk <= row.unit_cost_ek + EPS && !isLoss) {
      const below = row.unit_price_vk < row.unit_cost_ek - EPS;
      hints.push({
        ...base,
        id: `row_no_surcharge:${key}`,
        kind: 'row_no_surcharge',
        severity: below ? 'warn' : 'info',
        title: below ? `${label}: Material unter Einkaufspreis` : `${label}: ohne Materialaufschlag`,
        detail: `${prefix}Material-VK ${formatEuro(row.unit_price_vk)} bei Material-EK ${formatEuro(row.unit_cost_ek)} je ${row.unit || 'Einheit'}.`,
        amount: round2(Math.max(0, materialCosts - materialRevenue)),
      });
    }

    if (override != null && laborUnderCost) {
      const gapLoss = figures.laborHours * (realLaborCostRate - override);
      hints.push({
        ...base,
        id: `row_hourly_rate_below_cost:${key}`,
        kind: 'row_hourly_rate_below_cost',
        severity: 'warn',
        title: `${label}: Stundensatz unter Reallohnkosten`,
        detail: `${prefix}Abweichender Satz ${formatRate(override)} bei ${formatRate(realLaborCostRate)} Reallohnkosten (${formatEuro(-gapLoss)}).`,
        amount: round2(gapLoss),
      });
    }

    // Eventualpositionen gehen nicht in die Marge ein, heben sie also auch nicht.
    if (!hourLabor && !row.isEventual && row.unit_cost_ek <= EPS && row.unit_price_vk > EPS) {
      hints.push({
        ...base,
        id: `row_vk_without_ek:${key}`,
        kind: 'row_vk_without_ek',
        severity: 'info',
        title: `${label}: Verkaufspreis ohne Einkaufspreis`,
        detail: `${prefix}${formatEuro(materialRevenue)} Material-VK zählen ohne Material-EK voll als Rohertrag und heben die Marge.`,
        amount: round2(materialRevenue),
      });
    }

    const quote =
      !hourLabor && row.leadPositionId != null && input.quoteOffersByPositionId
        ? input.quoteOffersByPositionId[String(row.leadPositionId)]
        : undefined;
    if (quote && quote.cheapestEk != null && quote.cheapestEk < row.unit_cost_ek - EPS) {
      const savings = (row.unit_cost_ek - quote.cheapestEk) * row.quantity;
      hints.push({
        ...base,
        id: `cheaper_quote:${key}`,
        kind: 'cheaper_quote',
        severity: 'info',
        title: `${label}: günstigeres Lieferantenangebot`,
        detail: `${prefix}${quote.cheapestSupplier?.trim() || 'Ein Lieferant'} bietet ${formatEuro(quote.cheapestEk)} statt ${formatEuro(row.unit_cost_ek)} je ${row.unit || 'Einheit'}${savings > EPS ? ` — spart ${formatEuro(savings)}` : ''}.`,
        amount: round2(savings),
      });
    }
  });

  return hints;
}

/** Ein Eintrag in der Hinweis-Karte: ein Gesamt-Hinweis oder alle Positionen einer Art. */
export type OfferMarginHintGroup = {
  key: string;
  kind: OfferMarginHintKind;
  severity: OfferMarginHintSeverity;
  title: string;
  detail: string;
  hints: OfferMarginHint[];
  amount?: number;
};

function groupTitle(kind: OfferMarginHintKind, count: number): string {
  switch (kind) {
    case 'row_loss':
      return `${count} Positionen unter Kosten`;
    case 'row_no_surcharge':
      return `${count} Positionen ohne Materialaufschlag`;
    case 'row_vk_without_ek':
      return `${count} Positionen mit Verkaufspreis ohne Einkaufspreis`;
    case 'row_hourly_rate_below_cost':
      return `${count} Positionen mit Stundensatz unter Reallohnkosten`;
    case 'cheaper_quote':
      return `${count} günstigere Lieferantenangebote`;
    default:
      return `${count} Hinweise`;
  }
}

function groupDetail(kind: OfferMarginHintKind, amount: number): string {
  switch (kind) {
    case 'row_loss':
      return `Zusammen ${formatEuro(amount)} unter den Kosten.`;
    case 'row_no_surcharge':
      return 'Material-VK liegt auf oder unter dem Material-EK.';
    case 'row_vk_without_ek':
      return `${formatEuro(amount)} Material-VK zählen ohne Einkaufspreis voll als Rohertrag und heben die Marge.`;
    case 'row_hourly_rate_below_cost':
      return `Abweichende Stundensätze kosten zusammen ${formatEuro(amount)}.`;
    case 'cheaper_quote':
      return `Mögliche Ersparnis zusammen ${formatEuro(amount)}.`;
    default:
      return '';
  }
}

/**
 * Positions-Hinweise derselben Art zusammenfassen, damit große Angebote die Karte nicht fluten.
 * Warnungen zuerst, sonst Reihenfolge der Hinweise.
 */
export function groupOfferMarginHints(hints: readonly OfferMarginHint[]): OfferMarginHintGroup[] {
  const groups: OfferMarginHintGroup[] = [];
  const byKind = new Map<OfferMarginHintKind, OfferMarginHintGroup>();
  for (const hint of hints) {
    if (hint.rowIndex == null) {
      groups.push({
        key: hint.id,
        kind: hint.kind,
        severity: hint.severity,
        title: hint.title,
        detail: hint.detail,
        hints: [hint],
        ...(hint.amount != null ? { amount: hint.amount } : {}),
      });
      continue;
    }
    const existing = byKind.get(hint.kind);
    if (existing) {
      existing.hints.push(hint);
      if (hint.severity === 'warn') existing.severity = 'warn';
      continue;
    }
    const group: OfferMarginHintGroup = {
      key: `group:${hint.kind}`,
      kind: hint.kind,
      severity: hint.severity,
      title: hint.title,
      detail: hint.detail,
      hints: [hint],
    };
    byKind.set(hint.kind, group);
    groups.push(group);
  }
  for (const group of byKind.values()) {
    const amount = round2(group.hints.reduce((sum, hint) => sum + (hint.amount ?? 0), 0));
    if (group.hints.length === 1) {
      const only = group.hints[0]!;
      group.key = only.id;
      if (only.amount != null) group.amount = only.amount;
      continue;
    }
    group.title = groupTitle(group.kind, group.hints.length);
    group.detail = groupDetail(group.kind, amount);
    group.amount = amount;
  }
  const rank = (severity: OfferMarginHintSeverity) => (severity === 'warn' ? 0 : 1);
  return groups
    .map((group, order) => ({ group, order }))
    .sort((a, b) => rank(a.group.severity) - rank(b.group.severity) || a.order - b.order)
    .map(({ group }) => group);
}
